import { useCallback, useEffect, useRef, useState, type FormEvent, type KeyboardEvent as ReactKeyboardEvent, type PointerEvent as ReactPointerEvent } from 'react';
import { createPortal } from 'react-dom';
import {
  SOCIAL_POST_MAX_IMAGE_AUDIO_DURATION_SEC,
  SOCIAL_POST_MAX_VIDEO_DURATION_SEC,
  SOCIAL_STORY_IMAGE_DURATION_MS,
} from '../constants/socialPosts';
import { createOrGetConversation, sendConversationMessage } from '../api/messagingApi';
import { AssetRequestError } from '../api/getAssetById';
import { notifySocialPostsUpdated } from '../hooks/useSocialPosts';
import { useAuth } from '../auth/AuthContext';
import { useI18n } from '../i18n';
import type { TranslateFn } from '../i18n/operationLabels';
import { useAppNavigation } from '../navigation/AppNavigationContext';
import { useMessagingInbox } from '../messaging/MessagingInboxContext';
import { normalizeStoryUserId } from '../api/socialStoriesApi';
import { resolveStoryGroupAvatarSrc, resolveStoryUserAvatarSrc } from '../utils/profileAvatarDisplay';
import { rememberMessagingPeerDisplay } from '../utils/messagingPeerCache';
import { containsHebrewScript, resolveChatTextDir } from '../utils/textDirection';
import {
  deleteSocialPost,
  socialPostMediaUrls,
  type SocialPostRecord,
  type SocialPostUserGroup,
} from '../utils/socialPostsStorage';
import { ProfileAvatarImage } from './ProfileAvatarImage';
import styles from './SocialPostViewer.module.css';

type SocialPostViewerProps = {
  playlist: readonly SocialPostUserGroup[];
  startUserId: string;
  currentUserId: string;
  currentUserAvatarSrc: string;
  loop?: boolean;
  onClose: () => void;
};

type StoryMediaUrls = {
  image: string | null;
  audio: string | null;
  video: string | null;
};

const EMPTY_MEDIA: StoryMediaUrls = { image: null, audio: null, video: null };
const MUTE_STORAGE_KEY = 'ofrontend.socialStoryMuted';
const STORY_SWIPE_THRESHOLD_PX = 48;
const STORY_TAP_MOVE_MAX_PX = 12;
const STORY_LONG_PRESS_MS = 400;
/** Left / right thirds: left + center advance; right goes back. */
const STORY_EDGE_TAP_RATIO = 1 / 3;

type StoryMediaTouchState = {
  active: boolean;
  startX: number;
  startY: number;
  /** Physical screen zone at pointer-down: left / center / right. */
  zone: 'left' | 'center' | 'right';
  /** True once movement is clearly a swipe (not a tap). */
  swiping: boolean;
  /** True while long-press has paused playback. */
  holdPaused: boolean;
  longPressTimer: number | null;
};

/** Survives React Strict Mode remounts (JSX <audio> nodes do not). */
let sharedStoryAudio: HTMLAudioElement | null = null;
let sharedStoryAudioRetain = 0;

function acquireSharedStoryAudio(): HTMLAudioElement {
  sharedStoryAudioRetain += 1;
  if (!sharedStoryAudio) {
    sharedStoryAudio = new Audio();
    sharedStoryAudio.preload = 'auto';
  }
  return sharedStoryAudio;
}

function releaseSharedStoryAudio(): void {
  sharedStoryAudioRetain = Math.max(0, sharedStoryAudioRetain - 1);
  const snapshot = sharedStoryAudioRetain;
  queueMicrotask(() => {
    if (snapshot !== 0 || sharedStoryAudioRetain !== 0 || !sharedStoryAudio) {
      return;
    }
    hardStopMedia(sharedStoryAudio);
    sharedStoryAudio = null;
  });
}

function readStoredMuted(): boolean {
  try {
    return localStorage.getItem(MUTE_STORAGE_KEY) === '1';
  } catch {
    return false;
  }
}

function writeStoredMuted(muted: boolean): void {
  try {
    localStorage.setItem(MUTE_STORAGE_KEY, muted ? '1' : '0');
  } catch {
    /* ignore */
  }
}

function clamp01(value: number): number {
  if (!Number.isFinite(value)) {
    return 0;
  }
  return Math.min(1, Math.max(0, value));
}

function maxDurationSecForKind(mediaKind: SocialPostRecord['mediaKind']): number {
  return mediaKind === 'video'
    ? SOCIAL_POST_MAX_VIDEO_DURATION_SEC
    : SOCIAL_POST_MAX_IMAGE_AUDIO_DURATION_SEC;
}

/** Pause + detach media so orphaned HTMLAudioElement nodes cannot keep playing. */
function hardStopMedia(el: HTMLMediaElement | null | undefined): void {
  if (!el) {
    return;
  }
  try {
    el.pause();
    el.onended = null;
    el.ontimeupdate = null;
    el.onerror = null;
    el.oncanplay = null;
    el.removeAttribute('src');
    el.load();
  } catch {
    /* ignore */
  }
}

function clearMediaHandlers(el: HTMLMediaElement | null | undefined): void {
  if (!el) {
    return;
  }
  el.onended = null;
  el.ontimeupdate = null;
  el.onerror = null;
  el.oncanplay = null;
}

/** RTL only when the string contains Hebrew letters (not from UI language alone). */
function rtlForText(text: string): boolean {
  return containsHebrewScript(text);
}

function findPlaylistIndex(playlist: readonly SocialPostUserGroup[], userId: string): number {
  const idx = playlist.findIndex((group) => group.userId === userId);
  return idx >= 0 ? idx : 0;
}

export function SocialPostViewer({
  playlist,
  startUserId,
  currentUserId,
  currentUserAvatarSrc,
  loop = true,
  onClose,
}: SocialPostViewerProps) {
  const { t, language } = useI18n();
  const { isLoggedIn } = useAuth();
  const { openAuth, openMessages } = useAppNavigation();
  const { setConversations, refresh: refreshInbox } = useMessagingInbox();
  const [groupIndex, setGroupIndex] = useState(() => findPlaylistIndex(playlist, startUserId));
  const [index, setIndex] = useState(0);
  const [paused, setPaused] = useState(false);
  const [mediaFailed, setMediaFailed] = useState(false);
  const [muted, setMuted] = useState(readStoredMuted);
  const [audioFailed, setAudioFailed] = useState(false);
  const [segmentProgress, setSegmentProgress] = useState(0);
  const [removing, setRemoving] = useState(false);
  const [removeError, setRemoveError] = useState<string | null>(null);
  const [replyDraft, setReplyDraft] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [replyError, setReplyError] = useState<string | null>(null);
  const [replySent, setReplySent] = useState(false);
  const replyFocusPausedRef = useRef(false);

  const videoRef = useRef<HTMLVideoElement>(null);
  const advanceTimerRef = useRef<number | null>(null);
  const progressRafRef = useRef<number | null>(null);
  const imageTimerStartedAtRef = useRef<number | null>(null);
  const imageTimerDurationRef = useRef(SOCIAL_STORY_IMAGE_DURATION_MS);
  const imageRemainingMsRef = useRef(SOCIAL_STORY_IMAGE_DURATION_MS);
  const mutedRef = useRef(muted);
  mutedRef.current = muted;
  const pausedRef = useRef(paused);
  pausedRef.current = paused;
  const segmentProgressRef = useRef(0);
  segmentProgressRef.current = segmentProgress;
  const closedRef = useRef(false);
  const indexRef = useRef(index);
  indexRef.current = index;
  const groupIndexRef = useRef(groupIndex);
  groupIndexRef.current = groupIndex;
  const playlistRef = useRef(playlist);
  playlistRef.current = playlist;
  const playlistLenRef = useRef(playlist.length);
  playlistLenRef.current = playlist.length;
  const loopRef = useRef(loop);
  loopRef.current = loop;
  const postsLenRef = useRef(0);
  const sessionRef = useRef(0);
  const playingPostIdRef = useRef<string | null>(null);
  const mediaCacheRef = useRef<Map<string, StoryMediaUrls>>(new Map());
  const activeMediaPostIdRef = useRef<string | null>(null);
  const postsRef = useRef<SocialPostRecord[]>([]);
  const onCloseRef = useRef(onClose);
  onCloseRef.current = onClose;
  const mediaTouchRef = useRef<StoryMediaTouchState>({
    active: false,
    startX: 0,
    startY: 0,
    zone: 'center',
    swiping: false,
    holdPaused: false,
    longPressTimer: null,
  });

  const group = playlist[groupIndex] ?? playlist[0];
  const isOwner =
    group != null && normalizeStoryUserId(group.userId) === normalizeStoryUserId(currentUserId);
  const groupAvatarSrc = group
    ? resolveStoryUserAvatarSrc(
        group.userId,
        resolveStoryGroupAvatarSrc(group),
        currentUserId,
        currentUserAvatarSrc,
        group.posts,
        group.userLabel,
      )
    : currentUserAvatarSrc;
  const posts = group?.posts ?? [];
  postsRef.current = posts;
  postsLenRef.current = posts.length;
  const post = posts[index] ?? posts[0];
  const postId = post?.id ?? null;
  const mediaKind = post?.mediaKind ?? null;
  // Resolve in render (not a post-effect) so taps do not briefly show the previous story.
  const mediaUrls = (() => {
    if (!post) {
      return EMPTY_MEDIA;
    }
    const cached = mediaCacheRef.current.get(post.id);
    if (cached) {
      return cached;
    }
    const next = socialPostMediaUrls(post);
    mediaCacheRef.current.set(post.id, next);
    return next;
  })();
  const isHebrew = language === 'he';
  const textDir = isHebrew ? 'rtl' : 'ltr';

  const clearAdvanceTimer = useCallback(() => {
    if (advanceTimerRef.current !== null) {
      window.clearTimeout(advanceTimerRef.current);
      advanceTimerRef.current = null;
    }
  }, []);

  const clearProgressRaf = useCallback(() => {
    if (progressRafRef.current !== null) {
      window.cancelAnimationFrame(progressRafRef.current);
      progressRafRef.current = null;
    }
    imageTimerStartedAtRef.current = null;
  }, []);

  const hardStopActiveMedia = useCallback(() => {
    hardStopMedia(videoRef.current);
    hardStopMedia(sharedStoryAudio);
    playingPostIdRef.current = null;
  }, []);

  const handleClose = useCallback(() => {
    closedRef.current = true;
    sessionRef.current += 1;
    clearAdvanceTimer();
    clearProgressRaf();
    hardStopActiveMedia();
    onCloseRef.current();
  }, [clearAdvanceTimer, clearProgressRaf, hardStopActiveMedia]);

  const clearPaused = useCallback(() => {
    pausedRef.current = false;
    setPaused(false);
  }, []);

  const pauseStory = useCallback(() => {
    if (pausedRef.current) {
      return;
    }
    pausedRef.current = true;
    setPaused(true);
    clearAdvanceTimer();
    clearProgressRaf();
    videoRef.current?.pause();
    sharedStoryAudio?.pause();
    const started = imageTimerStartedAtRef.current;
    if (started !== null) {
      const elapsed = performance.now() - started;
      imageRemainingMsRef.current = Math.max(0, imageTimerDurationRef.current - elapsed);
      imageTimerStartedAtRef.current = null;
      return;
    }
    const activePost = postsRef.current[indexRef.current];
    const durationMs =
      activePost?.mediaKind === 'video'
        ? SOCIAL_POST_MAX_VIDEO_DURATION_SEC * 1000
        : SOCIAL_STORY_IMAGE_DURATION_MS;
    imageRemainingMsRef.current = Math.max(
      0,
      (1 - segmentProgressRef.current) * durationMs,
    );
  }, [clearAdvanceTimer, clearProgressRaf]);

  const resumeStory = useCallback(() => {
    if (!pausedRef.current) {
      return;
    }
    pausedRef.current = false;
    setPaused(false);
    sessionRef.current += 1;
  }, []);

  const goNextRef = useRef<() => void>(() => undefined);

  useEffect(() => {
    setGroupIndex(findPlaylistIndex(playlistRef.current, startUserId));
    setIndex(0);
    setSegmentProgress(0);
    setRemoveError(null);
    closedRef.current = false;
  }, [startUserId]);

  useEffect(() => {
    setReplyDraft('');
    setReplyError(null);
    setReplySent(false);
    replyFocusPausedRef.current = false;
  }, [group?.userId]);

  const sendStoryReply = useCallback(async () => {
    if (!group || isOwner || replySending) {
      return;
    }
    const text = replyDraft.trim();
    if (!text) {
      return;
    }
    if (!isLoggedIn) {
      openAuth('login');
      return;
    }
    const peerLabel = group.userLabel.trim() || group.userId;
    rememberMessagingPeerDisplay(group.userId, {
      label: peerLabel,
      avatarSrc: groupAvatarSrc,
    });
    setReplySending(true);
    setReplyError(null);
    try {
      const conversation = await createOrGetConversation(group.userId);
      const sent = await sendConversationMessage(conversation.id, text);
      setConversations((prev) => {
        const nextRow = {
          ...conversation,
          peerLabel: conversation.peerLabel || peerLabel,
          peerAvatarSrc: conversation.peerAvatarSrc || groupAvatarSrc,
          lastMessage: sent,
          updatedAt: sent.createdAt,
          unreadCount: 0,
        };
        if (prev.some((row) => row.id === conversation.id)) {
          return prev.map((row) => (row.id === conversation.id ? nextRow : row));
        }
        return [nextRow, ...prev];
      });
      setReplyDraft('');
      setReplySent(true);
      void refreshInbox({ soft: true });
    } catch (err) {
      const message =
        err instanceof AssetRequestError ? err.message : t('social.viewer.messageSendFailed');
      setReplyError(message);
    } finally {
      setReplySending(false);
    }
  }, [
    group,
    isOwner,
    replySending,
    replyDraft,
    isLoggedIn,
    openAuth,
    groupAvatarSrc,
    setConversations,
    refreshInbox,
    t,
  ]);

  const onReplyFocus = useCallback(() => {
    if (!isLoggedIn) {
      openAuth('login');
      return;
    }
    if (!pausedRef.current) {
      replyFocusPausedRef.current = true;
      pauseStory();
    }
  }, [isLoggedIn, openAuth, pauseStory]);

  const onReplyBlur = useCallback(() => {
    if (replyFocusPausedRef.current) {
      replyFocusPausedRef.current = false;
      resumeStory();
    }
  }, [resumeStory]);

  const onReplyKeyDown = useCallback(
    (event: ReactKeyboardEvent<HTMLInputElement>) => {
      event.stopPropagation();
      if (event.key === 'Enter' && !event.shiftKey) {
        event.preventDefault();
        void sendStoryReply();
      }
    },
    [sendStoryReply],
  );

  const onReplySubmit = useCallback(
    (event: FormEvent) => {
      event.preventDefault();
      event.stopPropagation();
      void sendStoryReply();
    },
    [sendStoryReply],
  );

  const openOwnerChat = useCallback(() => {
    if (!group || isOwner) {
      return;
    }
    const peerLabel = group.userLabel.trim() || group.userId;
    rememberMessagingPeerDisplay(group.userId, {
      label: peerLabel,
      avatarSrc: groupAvatarSrc,
    });
    // Do not history.back() — that races AppNavigation and snaps back to Home.
    closedRef.current = true;
    sessionRef.current += 1;
    clearAdvanceTimer();
    clearProgressRaf();
    hardStopActiveMedia();
    openMessages(group.userId);
  }, [
    group,
    isOwner,
    groupAvatarSrc,
    clearAdvanceTimer,
    clearProgressRaf,
    hardStopActiveMedia,
    openMessages,
  ]);


  useEffect(() => {
    if (playlist.length === 0) {
      handleClose();
      return;
    }
    if (groupIndex >= playlist.length) {
      setGroupIndex(Math.max(0, playlist.length - 1));
      setIndex(0);
    }
  }, [playlist, groupIndex, handleClose]);

  useEffect(() => {
    if (index >= posts.length && posts.length > 0) {
      setIndex(posts.length - 1);
    }
  }, [index, posts.length]);

  useEffect(() => {
    const audio = acquireSharedStoryAudio();
    return () => {
      clearMediaHandlers(audio);
      releaseSharedStoryAudio();
    };
  }, []);

  // Reset playback flags when the active story changes.
  useEffect(() => {
    activeMediaPostIdRef.current = postId;
    playingPostIdRef.current = null;
    setMediaFailed(false);
    setAudioFailed(false);
    setSegmentProgress(0);
    setRemoveError(null);
    imageRemainingMsRef.current = SOCIAL_STORY_IMAGE_DURATION_MS;
  }, [postId]);

  useEffect(() => {
    const prevOverflow = document.body.style.overflow;
    document.body.style.overflow = 'hidden';
    const cache = mediaCacheRef.current;
    return () => {
      document.body.style.overflow = prevOverflow;
      closedRef.current = true;
      sessionRef.current += 1;
      clearAdvanceTimer();
      clearProgressRaf();
      hardStopActiveMedia();
      cache.clear();
      activeMediaPostIdRef.current = null;
    };
  }, [clearAdvanceTimer, clearProgressRaf, hardStopActiveMedia]);

  useEffect(() => {
    const onKeyDown = (event: KeyboardEvent) => {
      if (event.key !== 'Escape') {
        return;
      }
      const target = event.target;
      if (
        target instanceof HTMLElement &&
        (target.tagName === 'INPUT' || target.tagName === 'TEXTAREA')
      ) {
        return;
      }
      handleClose();
    };
    document.addEventListener('keydown', onKeyDown);
    return () => document.removeEventListener('keydown', onKeyDown);
  }, [handleClose]);

  const applyMuteToElements = useCallback((nextMuted: boolean) => {
    if (videoRef.current) {
      videoRef.current.muted = nextMuted;
    }
    if (sharedStoryAudio) {
      sharedStoryAudio.muted = nextMuted;
    }
  }, []);

  useEffect(() => {
    applyMuteToElements(muted);
  }, [muted, applyMuteToElements, mediaUrls.audio, mediaUrls.video, index]);

  const toggleMuted = useCallback(() => {
    setMuted((prev) => {
      const next = !prev;
      writeStoredMuted(next);
      applyMuteToElements(next);
      return next;
    });
  }, [applyMuteToElements]);

  const advanceToNextGroup = useCallback((): boolean => {
    const gi = groupIndexRef.current;
    const len = playlistLenRef.current;
    if (len <= 0) {
      return false;
    }
    if (gi < len - 1) {
      setGroupIndex(gi + 1);
      setIndex(0);
      return true;
    }
    if (loopRef.current) {
      setGroupIndex(0);
      setIndex(0);
      return true;
    }
    return false;
  }, []);

  const retreatToPrevGroup = useCallback((): boolean => {
    const gi = groupIndexRef.current;
    const pl = playlistRef.current;
    if (pl.length <= 0) {
      return false;
    }
    if (gi > 0) {
      const prev = gi - 1;
      setGroupIndex(prev);
      setIndex(Math.max(0, pl[prev].posts.length - 1));
      return true;
    }
    if (loopRef.current) {
      const last = pl.length - 1;
      setGroupIndex(last);
      setIndex(Math.max(0, pl[last].posts.length - 1));
      return true;
    }
    return false;
  }, []);

  const goNext = useCallback(() => {
    if (closedRef.current) {
      return;
    }
    clearPaused();
    clearAdvanceTimer();
    clearProgressRaf();
    hardStopActiveMedia();
    setSegmentProgress(0);

    const current = indexRef.current;
    const total = postsLenRef.current;
    if (current >= total - 1) {
      if (!advanceToNextGroup()) {
        handleClose();
      }
      return;
    }
    setIndex(current + 1);
  }, [advanceToNextGroup, clearAdvanceTimer, clearProgressRaf, clearPaused, hardStopActiveMedia, handleClose]);
  goNextRef.current = goNext;

  const goPrev = useCallback(() => {
    if (closedRef.current) {
      return;
    }
    clearPaused();
    clearAdvanceTimer();
    clearProgressRaf();
    hardStopActiveMedia();
    setSegmentProgress(0);
    const current = indexRef.current;
    if (current > 0) {
      setIndex(current - 1);
      return;
    }
    retreatToPrevGroup();
  }, [clearAdvanceTimer, clearProgressRaf, clearPaused, hardStopActiveMedia, retreatToPrevGroup]);

  /** Jump to the next account's first story (horizontal swipe left). */
  const skipToNextAccount = useCallback(() => {
    if (closedRef.current) {
      return;
    }
    clearPaused();
    clearAdvanceTimer();
    clearProgressRaf();
    hardStopActiveMedia();
    setSegmentProgress(0);
    if (!advanceToNextGroup()) {
      handleClose();
    }
  }, [advanceToNextGroup, clearAdvanceTimer, clearProgressRaf, clearPaused, hardStopActiveMedia, handleClose]);

  /** Jump to the previous account's last story (horizontal swipe right). */
  const skipToPrevAccount = useCallback(() => {
    if (closedRef.current) {
      return;
    }
    clearPaused();
    clearAdvanceTimer();
    clearProgressRaf();
    hardStopActiveMedia();
    setSegmentProgress(0);
    retreatToPrevGroup();
  }, [clearAdvanceTimer, clearProgressRaf, clearPaused, hardStopActiveMedia, retreatToPrevGroup]);

  const clearLongPressTimer = useCallback(() => {
    const touch = mediaTouchRef.current;
    if (touch.longPressTimer !== null) {
      window.clearTimeout(touch.longPressTimer);
      touch.longPressTimer = null;
    }
  }, []);

  const touchZoneForX = (clientX: number, layer: HTMLDivElement): 'left' | 'center' | 'right' => {
    const rect = layer.getBoundingClientRect();
    if (rect.width <= 0) {
      return 'center';
    }
    const xRatio = (clientX - rect.left) / rect.width;
    if (xRatio < STORY_EDGE_TAP_RATIO) {
      return 'left';
    }
    if (xRatio > 1 - STORY_EDGE_TAP_RATIO) {
      return 'right';
    }
    return 'center';
  };

  const handleMediaPointerDown = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      if (event.button !== 0) {
        return;
      }
      const layer = event.currentTarget;
      const zone = touchZoneForX(event.clientX, layer);
      clearLongPressTimer();
      mediaTouchRef.current = {
        active: true,
        startX: event.clientX,
        startY: event.clientY,
        zone,
        swiping: false,
        holdPaused: false,
        longPressTimer: window.setTimeout(() => {
          mediaTouchRef.current.longPressTimer = null;
          if (!mediaTouchRef.current.active || mediaTouchRef.current.swiping) {
            return;
          }
          pauseStory();
          mediaTouchRef.current.holdPaused = true;
        }, STORY_LONG_PRESS_MS),
      };
      layer.setPointerCapture(event.pointerId);
    },
    [clearLongPressTimer, pauseStory],
  );

  const handleMediaPointerMove = useCallback((event: ReactPointerEvent<HTMLDivElement>) => {
    const touch = mediaTouchRef.current;
    if (!touch.active) {
      return;
    }
    const dx = event.clientX - touch.startX;
    const dy = event.clientY - touch.startY;
    if (
      !touch.swiping &&
      (Math.abs(dx) >= STORY_TAP_MOVE_MAX_PX || Math.abs(dy) >= STORY_TAP_MOVE_MAX_PX)
    ) {
      touch.swiping = true;
      clearLongPressTimer();
      if (touch.holdPaused) {
        resumeStory();
        touch.holdPaused = false;
      }
    }
  }, [clearLongPressTimer, resumeStory]);

  const handleMediaPointerEnd = useCallback(
    (event: ReactPointerEvent<HTMLDivElement>) => {
      const touch = mediaTouchRef.current;
      if (!touch.active) {
        return;
      }
      const layer = event.currentTarget;
      if (layer.hasPointerCapture(event.pointerId)) {
        layer.releasePointerCapture(event.pointerId);
      }

      const dx = event.clientX - touch.startX;
      const dy = event.clientY - touch.startY;
      const wasHoldPause = touch.holdPaused;
      clearLongPressTimer();
      touch.active = false;
      touch.holdPaused = false;

      if (wasHoldPause) {
        resumeStory();
        return;
      }

      if (touch.swiping && Math.abs(dy) >= STORY_SWIPE_THRESHOLD_PX && Math.abs(dy) > Math.abs(dx)) {
        if (dy > 0) {
          handleClose();
        }
        return;
      }

      if (touch.swiping && Math.abs(dx) >= STORY_SWIPE_THRESHOLD_PX && Math.abs(dx) > Math.abs(dy)) {
        if (dx < 0) {
          skipToNextAccount();
        } else {
          skipToPrevAccount();
        }
        return;
      }

      if (Math.abs(dx) > STORY_TAP_MOVE_MAX_PX || Math.abs(dy) > STORY_TAP_MOVE_MAX_PX) {
        return;
      }

      if (touch.zone === 'right') {
        goPrev();
        return;
      }
      goNext();
    },
    [
      clearLongPressTimer,
      goNext,
      goPrev,
      handleClose,
      resumeStory,
      skipToNextAccount,
      skipToPrevAccount,
    ],
  );

  const handleRemove = useCallback(async () => {
    if (!isOwner || !post || removing) {
      return;
    }
    const confirmed = window.confirm(t('social.viewer.removeConfirm'));
    if (!confirmed) {
      return;
    }

    const deletedId = post.id;
    const wasLast = postsLenRef.current <= 1;
    const nextIndex = Math.min(indexRef.current, Math.max(0, postsLenRef.current - 2));

    setRemoving(true);
    setRemoveError(null);
    clearAdvanceTimer();
    clearProgressRaf();
    hardStopActiveMedia();

    try {
      await deleteSocialPost(deletedId);
      mediaCacheRef.current.delete(deletedId);
      notifySocialPostsUpdated();
      if (wasLast) {
        if (playlistRef.current.length <= 1) {
          handleClose();
          return;
        }
        setIndex(0);
        return;
      }
      setIndex(nextIndex);
    } catch {
      setRemoveError(t('social.viewer.removeFailed'));
    } finally {
      setRemoving(false);
    }
  }, [
    isOwner,
    post,
    removing,
    t,
    clearAdvanceTimer,
    clearProgressRaf,
    hardStopActiveMedia,
    handleClose,
  ]);

  const trackMediaProgress = useCallback(
    (el: HTMLMediaElement, maxSec: number, session: number) => {
      clearProgressRaf();
      const tick = () => {
        if (closedRef.current || sessionRef.current !== session || pausedRef.current) {
          return;
        }
        const duration = Math.min(
          Number.isFinite(el.duration) && el.duration > 0 ? el.duration : maxSec,
          maxSec,
        );
        setSegmentProgress(clamp01(el.currentTime / duration));
        if (!el.ended && el.currentTime < maxSec) {
          progressRafRef.current = window.requestAnimationFrame(tick);
        }
      };
      progressRafRef.current = window.requestAnimationFrame(tick);
    },
    [clearProgressRaf],
  );

  const trackTimedProgress = useCallback(
    (durationMs: number, session: number) => {
      clearProgressRaf();
      imageTimerDurationRef.current = durationMs;
      imageTimerStartedAtRef.current = performance.now();
      const tick = () => {
        if (closedRef.current || sessionRef.current !== session || pausedRef.current) {
          return;
        }
        const started = imageTimerStartedAtRef.current;
        if (started === null) {
          return;
        }
        const elapsed = performance.now() - started;
        setSegmentProgress(clamp01(elapsed / imageTimerDurationRef.current));
        if (elapsed < imageTimerDurationRef.current) {
          progressRafRef.current = window.requestAnimationFrame(tick);
        }
      };
      progressRafRef.current = window.requestAnimationFrame(tick);
    },
    [clearProgressRaf],
  );

  const tryPlay = useCallback(async (el: HTMLMediaElement): Promise<boolean> => {
    el.muted = mutedRef.current;
    try {
      await el.play();
      return true;
    } catch {
      if (!el.muted) {
        el.muted = true;
        mutedRef.current = true;
        setMuted(true);
        writeStoredMuted(true);
        try {
          await el.play();
          return true;
        } catch {
          return false;
        }
      }
      return false;
    }
  }, []);

  useEffect(() => {
    if (closedRef.current || removing || paused) {
      return undefined;
    }

    if (!postId || !mediaKind) {
      return undefined;
    }

    const session = ++sessionRef.current;
    const maxSec = maxDurationSecForKind(mediaKind);
    const audioSrc = mediaUrls.audio;
    const videoSrc = mediaUrls.video;
    clearAdvanceTimer();
    clearProgressRaf();

    const advance = () => {
      goNextRef.current();
    };

    const scheduleAdvance = (delayMs: number) => {
      clearAdvanceTimer();
      trackTimedProgress(delayMs, session);
      advanceTimerRef.current = window.setTimeout(() => {
        advanceTimerRef.current = null;
        if (closedRef.current || sessionRef.current !== session || pausedRef.current) {
          return;
        }
        advance();
      }, delayMs);
    };

    if (mediaFailed) {
      setSegmentProgress(0);
      scheduleAdvance(imageRemainingMsRef.current || SOCIAL_STORY_IMAGE_DURATION_MS);
      return () => {
        if (sessionRef.current === session) {
          clearAdvanceTimer();
          clearProgressRaf();
        }
      };
    }

    let cancelled = false;
    let retryFrame = 0;

    const bindHandlers = (el: HTMLMediaElement) => {
      el.muted = mutedRef.current;
      el.onended = () => {
        if (!cancelled && sessionRef.current === session && !pausedRef.current) {
          advance();
        }
      };
      el.ontimeupdate = () => {
        if (cancelled || sessionRef.current !== session || pausedRef.current) {
          return;
        }
        if (el.currentTime >= maxSec) {
          el.pause();
          advance();
        }
      };
    };

    const bindAndPlay = (el: HTMLMediaElement, src: string) => {
      bindHandlers(el);

      const sameSrc = el.getAttribute('src') === src || el.src === src;
      const samePost = playingPostIdRef.current === postId;
      const keepPlaying =
        sameSrc && samePost && !el.paused && el.currentTime > 0;

      if (keepPlaying) {
        trackMediaProgress(el, maxSec, session);
        return;
      }

      if (!sameSrc) {
        el.src = src;
        el.currentTime = 0;
      } else if (!samePost) {
        el.currentTime = 0;
      }

      setSegmentProgress(0);
      void tryPlay(el).then((ok) => {
        if (cancelled || sessionRef.current !== session) {
          return;
        }
        if (!ok) {
          if (mediaKind === 'imageAudio') {
            setAudioFailed(true);
          }
          scheduleAdvance(imageRemainingMsRef.current || SOCIAL_STORY_IMAGE_DURATION_MS);
          return;
        }
        playingPostIdRef.current = postId;
        trackMediaProgress(el, maxSec, session);
      });
    };

    const startCurrentStory = () => {
      if (cancelled || closedRef.current || sessionRef.current !== session || pausedRef.current) {
        return;
      }

      if (mediaKind === 'video' && videoSrc) {
        const video = videoRef.current;
        if (!video) {
          retryFrame = window.requestAnimationFrame(startCurrentStory);
          return;
        }
        bindAndPlay(video, videoSrc);
        return;
      }

      if (mediaKind === 'imageAudio') {
        if (audioSrc && !audioFailed) {
          const audio = sharedStoryAudio ?? acquireSharedStoryAudio();
          bindAndPlay(audio, audioSrc);
          return;
        }
        setSegmentProgress(0);
        scheduleAdvance(imageRemainingMsRef.current || SOCIAL_STORY_IMAGE_DURATION_MS);
      }
    };

    startCurrentStory();

    return () => {
      cancelled = true;
      if (retryFrame) {
        window.cancelAnimationFrame(retryFrame);
      }
      if (sessionRef.current === session) {
        clearAdvanceTimer();
        clearProgressRaf();
        // Leave playback running across Strict Mode / callback-identity remounts.
        clearMediaHandlers(videoRef.current);
        clearMediaHandlers(sharedStoryAudio);
      }
    };
  }, [
    postId,
    mediaKind,
    mediaUrls.audio,
    mediaUrls.video,
    mediaFailed,
    audioFailed,
    removing,
    clearAdvanceTimer,
    clearProgressRaf,
    tryPlay,
    trackMediaProgress,
    trackTimedProgress,
    paused,
  ]);

  if (!post || !group || typeof document === 'undefined') {
    return null;
  }

  const atFirstStory = groupIndex === 0 && index === 0;
  const atLastStory = index >= posts.length - 1;
  const canLoop = loop && playlist.length > 1;

  const hasImage = Boolean(mediaUrls.image) && !mediaFailed;
  const hasVideo = post.mediaKind === 'video' && Boolean(mediaUrls.video) && !mediaFailed;
  const hasSound =
    hasVideo || (post.mediaKind === 'imageAudio' && Boolean(mediaUrls.audio) && !audioFailed);
  const captionText = post.text.trim();
  const captionRtl = rtlForText(captionText);
  const headerRtl = rtlForText(group.userLabel);

  return createPortal(
    <div
      className={styles.overlay}
      role="presentation"
      dir={textDir}
      lang={isHebrew ? 'he' : 'en'}
    >
      <div className={styles.desktopCarousel} onClick={(event) => event.stopPropagation()}>
        <button
          type="button"
          className={styles.desktopNavButton}
          aria-label={t('social.viewer.prev')}
          disabled={!canLoop && atFirstStory}
          onClick={(event) => {
            event.stopPropagation();
            goPrev();
          }}
        >
          {'<'}
        </button>
        <div
          className={styles.viewer}
          role="dialog"
          aria-modal="true"
          aria-label={group.userLabel}
          dir={textDir}
          lang={isHebrew ? 'he' : 'en'}
        >
        <div className={styles.progressRow} aria-hidden>
          {posts.map((item: SocialPostRecord, i: number) => {
            const fill = i < index ? 1 : i === index ? segmentProgress : 0;
            return (
              <span key={item.id} className={styles.progressSegment}>
                <span
                  className={styles.progressSegmentFill}
                  style={{ transform: `scaleX(${fill})` }}
                />
              </span>
            );
          })}
        </div>

        <header className={styles.header}>
          <ProfileAvatarImage src={groupAvatarSrc} className={styles.headerAvatar} />
          <div
            className={[styles.headerText, headerRtl ? styles.textRtl : styles.textLtr].join(' ')}
            dir={headerRtl ? 'rtl' : 'ltr'}
          >
            <span className={styles.headerName}>{group.userLabel}</span>
            <span className={styles.headerMeta}>{formatPostAge(post.createdAt, t)}</span>
          </div>
          {isOwner ? (
            <button
              type="button"
              className={styles.removeButton}
              onClick={() => {
                void handleRemove();
              }}
              disabled={removing}
              aria-label={t('social.viewer.remove')}
              title={t('social.viewer.remove')}
            >
              {removing ? '…' : <TrashIcon />}
            </button>
          ) : null}
          {hasSound ? (
            <button
              type="button"
              className={styles.muteButton}
              onClick={toggleMuted}
              aria-pressed={muted}
              aria-label={muted ? t('social.viewer.unmute') : t('social.viewer.mute')}
              title={muted ? t('social.viewer.unmute') : t('social.viewer.mute')}
            >
              {muted ? <MuteIcon /> : <UnmuteIcon />}
            </button>
          ) : null}
        </header>

        <div className={styles.mediaStage}>
          <div
            className={styles.mediaTouchLayer}
            role="presentation"
            onPointerDown={handleMediaPointerDown}
            onPointerMove={handleMediaPointerMove}
            onPointerUp={handleMediaPointerEnd}
            onPointerCancel={handleMediaPointerEnd}
          />
          {hasVideo ? (
            <video
              key={`video-${postId}`}
              ref={videoRef}
              className={styles.video}
              playsInline
              muted={muted}
              onError={() => setMediaFailed(true)}
            />
          ) : hasImage ? (
            <img
              key={`image-${postId}`}
              src={mediaUrls.image ?? undefined}
              alt=""
              className={styles.image}
              onError={() => setMediaFailed(true)}
            />
          ) : (
            <div className={styles.mediaFallback} aria-hidden>
              <ProfileAvatarImage src={groupAvatarSrc} className={styles.fallbackAvatar} />
            </div>
          )}
        </div>

        {captionText ? (
          <div className={styles.captionBlock}>
            <p
              className={captionRtl ? styles.captionRtl : styles.caption}
              dir={captionRtl ? 'rtl' : 'ltr'}
              lang={captionRtl ? 'he' : undefined}
            >
              {captionText}
            </p>
          </div>
        ) : null}
        {!isOwner ? (
          <div className={styles.messageBar}>
            <form className={styles.messageForm} onSubmit={onReplySubmit}>
              <input
                className={styles.messageInput}
                dir={resolveChatTextDir(replyDraft, language)}
                value={replyDraft}
                onChange={(event) => {
                  setReplyDraft(event.target.value);
                  setReplySent(false);
                  setReplyError(null);
                }}
                onFocus={onReplyFocus}
                onBlur={onReplyBlur}
                onKeyDown={onReplyKeyDown}
                onClick={(event) => event.stopPropagation()}
                onPointerDown={(event) => event.stopPropagation()}
                placeholder={t('social.viewer.messagePlaceholder', {
                  name: group.userLabel.trim() || group.userId,
                })}
                maxLength={4000}
                disabled={replySending}
                enterKeyHint="send"
                aria-label={t('social.viewer.message')}
              />
              <button
                type="submit"
                className={styles.messageSendButton}
                disabled={replySending || !replyDraft.trim()}
                aria-label={t('social.viewer.messageSend')}
              >
                {replySending ? '…' : <MessageIcon />}
              </button>
            </form>
            {replyError ? (
              <p className={styles.messageStatusError} role="alert">
                {replyError}
              </p>
            ) : null}
            {replySent && !replyError ? (
              <p className={styles.messageStatusOk} role="status">
                <span>{t('social.viewer.messageSent')}</span>
                <button type="button" className={styles.messageOpenChat} onClick={openOwnerChat}>
                  {t('social.viewer.openChat')}
                </button>
              </p>
            ) : null}
          </div>
        ) : null}
        {removeError ? (
          <p className={styles.removeError} role="alert">
            {removeError}
          </p>
        ) : null}
        </div>
        <button
          type="button"
          className={styles.desktopNavButton}
          aria-label={
            !canLoop && atLastStory && groupIndex >= playlist.length - 1
              ? t('social.viewer.close')
              : t('social.viewer.next')
          }
          onClick={(event) => {
            event.stopPropagation();
            goNext();
          }}
        >
          {'>'}
        </button>
      </div>
    </div>,
    document.body,
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" width="16" height="16" aria-hidden>
      <path
        fill="currentColor"
        d="M9 3h6l1 2h4v2H4V5h4l1-2zm1 6h2v9h-2V9zm4 0h2v9h-2V9zM7 9h2v9H7V9z"
      />
    </svg>
  );
}

function MessageIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" fill="currentColor" aria-hidden>
      <path d="M12 2C6.477 2 2 6.145 2 11.243c0 2.908 1.434 5.502 3.676 7.183V22l3.358-1.843c.896.248 1.844.382 2.966.382 5.523 0 10-4.145 10-9.243S17.523 2 12 2zm1.053 12.446-2.568-2.742-5.019 2.742 5.524-5.873 2.637 2.742 4.947-2.742-5.521 5.873z" />
    </svg>
  );
}

function MuteIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
      <path
        fill="currentColor"
        d="M4.3 3.7 3 5l4.6 4.6H3v4.8h4.6L13 20v-7.2L18.7 18.5 20 17.2 4.3 3.7zm8.7 2.2-2.5 2.5L13 10.9V5.9zM19.1 12c0-1.7-.6-3.2-1.6-4.4l-1.1 1.1c.7.9 1.1 2 1.1 3.3 0 .6-.1 1.2-.3 1.8l1.2 1.2c.4-.9.7-1.9.7-3z"
      />
    </svg>
  );
}

function UnmuteIcon() {
  return (
    <svg viewBox="0 0 24 24" width="18" height="18" aria-hidden>
      <path
        fill="currentColor"
        d="M3 9.6v4.8h3.8L12 20V4L6.8 9.6H3zm12.3 2.4c0-1.7-.9-3.2-2.2-4v8c1.3-.8 2.2-2.3 2.2-4zm2.2 0c0 2.6-1.4 4.8-3.5 6l1 1.7c2.7-1.5 4.5-4.4 4.5-7.7S17.7 6 15 4.5L14 6.2c2.1 1.2 3.5 3.4 3.5 6z"
      />
    </svg>
  );
}

function formatPostAge(createdAt: number, t: TranslateFn): string {
  const hours = Math.max(1, Math.round((Date.now() - createdAt) / (60 * 60 * 1000)));
  if (hours < 24) {
    return t('social.viewer.hoursAgo', { hours: String(hours) });
  }
  const days = Math.round(hours / 24);
  return t('social.viewer.daysAgo', { days: String(days) });
}
